import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import SectionHeading from "@/components/feature/SectionHeading";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { paginate, sortMovies } from "@/utils/movie";
import { useSeo } from "@/hooks/useSeo";

const LATEST_PAGE_SIZE = 10;

export default function Home() {
  const [latestPage, setLatestPage] = useState(1);

  useSeo({
    title: "红桃视频在线观看 - 优质影视在线观看平台",
    description:
      "红桃视频在线观看提供丰富的影视资源，包括电影、电视剧和综艺，满足用户多样化的在线观看需求。",
    keywords: "红桃视频在线观看,影视在线观看,电影,电视剧,高清视频,在线视频,影视平台",
    canonicalPath: "/",
  });

  const featured = useMemo(() => movies.filter((m) => m.featured), []);
  const latest = useMemo(() => sortMovies(movies, "createdAt"), []);
  const popular = useMemo(() => sortMovies(movies, "popularity").slice(0, 10), []);
  const topRated = useMemo(() => sortMovies(movies, "rating").slice(0, 6), []);

  const latestTotalPages = Math.ceil(latest.length / LATEST_PAGE_SIZE);
  const latestItems = paginate(latest, latestPage, LATEST_PAGE_SIZE);

  const handleLatestPage = (p: number) => {
    setLatestPage(p);
    const el = document.getElementById("latest-section");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const stats = [
    { value: "1,200+", label: "收录影视作品" },
    { value: "3 大类", label: "电影 · 剧集 · 综艺" },
    { value: "每日更新", label: "最新上线内容" },
    { value: "全免费", label: "信息浏览无门槛" },
  ];

  return (
    <div className="w-full">
      <HeroCarousel items={featured} />

      {/* Category quick entries */}
      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <SectionHeading
          title="影视分类"
          subtitle="按类别快速进入，找到你感兴趣的影视内容"
          icon="ri-apps-2-line"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((cat) => {
            const count = movies.filter((m) => m.category === cat.key).length;
            return (
              <Link
                key={cat.key}
                to={`/category/${cat.key}`}
                className="group flex cursor-pointer items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-4 transition-all hover:-translate-y-1 hover:border-primary-300 md:p-5"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={`${cat.icon} text-2xl`} />
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-medium text-foreground-950">{cat.name}</h3>
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                      {count} 部
                    </span>
                  </div>
                  <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-500">
                    {cat.desc}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Latest */}
      <section id="latest-section" className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-14">
        <SectionHeading
          title="最新上线"
          subtitle="近期新增的影视作品，看看有什么新片"
          icon="ri-time-line"
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {latestItems.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
        <div className="mt-8">
          <Pagination
            currentPage={latestPage}
            totalPages={latestTotalPages}
            onChange={handleLatestPage}
          />
        </div>
      </section>

      {/* Popular */}
      <section className="w-full px-4 py-10 md:px-8 md:py-14">
        <SectionHeading
          title="热门影视精选"
          subtitle="播放热度领先的口碑之作"
          icon="ri-fire-line"
          moreTo="/category/movie"
          moreLabel="更多热门"
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {popular.map((movie, i) => (
            <MovieCard key={movie.id} movie={movie} rank={i + 1} />
          ))}
        </div>
      </section>

      {/* Top rated */}
      <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-14">
        <SectionHeading
          title="高分口碑榜"
          subtitle="用户评分最高的影视，值得反复回味"
          icon="ri-award-line"
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {topRated.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      {/* Intro */}
      <section className="w-full px-4 py-10 md:px-8 md:py-16">
        <div className="overflow-hidden rounded-lg border border-background-200 bg-secondary-50">
          <div className="grid grid-cols-1 gap-8 p-6 md:p-10 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">
                关于红桃视频在线观看
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                红桃视频在线观看是一个专注影视内容展示的平台，聚合电影、电视剧与综艺的详细信息。
                我们致力于让每一位影视爱好者都能快速搜索、分类浏览并了解感兴趣作品的完整资料。
                平台界面简洁、信息层级清晰，帮助你高效地发现下一部想看的作品。
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                了解我们
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-s-line" />
                </span>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s) => (
                <div
                  key={s.label}
                  className="flex flex-col justify-center rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="font-heading text-2xl text-primary-600 md:text-3xl">
                    {s.value}
                  </span>
                  <span className="mt-1 text-sm text-foreground-500">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}